// Phase 3 of the telemetry plan: the state-timeline player (option B in §5).
//
// What this is: a scrubber over one session's reconstructed STATE — mode, data
// source and every filter in force — so you can watch somebody's reasoning
// unfold instead of reading a list of clicks.
//
// What it is NOT: a recording of their screen. D-T4 chose this over rrweb
// precisely so nothing stores a copy of the customer's display. Nor does it
// re-mount the live Safety Dashboard at each step; SafetyOverview holds its
// state internally with no URL representation, so there is nothing to drive it
// from. What you see here is the state we recorded, rendered as state.
//
// Everything comes from the trace the server already returns — no new endpoint,
// no new storage. That is the whole appeal of option B.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fmtDuration } from './usageUi';

// A real session has minutes of idle in it. Played at true speed the scrubber
// would sit still for whole minutes, so a gap is compressed to this and called
// out instead — the pause is information, the waiting is not.
const MAX_FRAME_MS = 1500;
const SPEEDS = [1, 4, 16];

/** Reconstruct the state in force at every step.
 *
 *  Events carry only what they changed, so a click event has no filter set of
 *  its own and `mode` may be absent. The state at step N is therefore the last
 *  thing we were told, carried forward — which is exactly what "replay" means
 *  here. `changed` marks the keys that moved at this step so the panel can show
 *  the user's attention rather than making you diff two screens by eye.
 */
export function buildFrames(steps) {
  const frames = [];
  let mode = null;
  let source = null;
  let filters = {};
  (steps || []).forEach((s) => {
    if (s.mode) mode = s.mode;
    if (s.source) source = s.source;
    const next = s.filters && Object.keys(s.filters).length ? s.filters : filters;
    const changed = next === filters ? [] : changedKeys(filters, next);
    filters = next;
    frames.push({ step: s, mode, source, filters, changed });
  });
  return frames;
}

function changedKeys(before, after) {
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  return [...keys].filter((k) => JSON.stringify(before?.[k]) !== JSON.stringify(after?.[k]));
}

function showValue(v) {
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (v === true) return 'on';
  if (v === false) return 'off';
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

const SessionTimeline = ({ steps }) => {
  const frames = useMemo(() => buildFrames(steps), [steps]);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(4);
  const timer = useRef(null);

  const last = frames.length - 1;
  // A new session invalidates the cursor; starting mid-way through somebody
  // else's session would be meaningless.
  useEffect(() => { setI(0); setPlaying(false); }, [steps]);

  const clearTimer = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => {
    if (!playing) { clearTimer(); return undefined; }
    if (i >= last) { setPlaying(false); return undefined; }
    const here = frames[i].step;
    const next = frames[i + 1].step;
    const realMs = Math.max(0, (next.offset_s - here.offset_s) * 1000);
    timer.current = setTimeout(() => setI((n) => Math.min(last, n + 1)),
      Math.min(MAX_FRAME_MS, realMs / speed));
    return clearTimer;
  }, [playing, i, last, frames, speed, clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  if (!frames.length) return null;
  const f = frames[i];
  const keys = Object.keys(f.filters).sort();

  return (
    <div className="ua-player">
      <div className="ua-player-transport">
        <button type="button" className="ua-topn" title="Back to the start"
          onClick={() => { setPlaying(false); setI(0); }}>⏮</button>
        <button type="button" className="ua-topn" title="Previous action"
          disabled={i === 0}
          onClick={() => { setPlaying(false); setI((n) => Math.max(0, n - 1)); }}>◀</button>
        <button type="button" className={`ua-topn${playing ? ' on' : ''}`}
          onClick={() => setPlaying((p) => !p)}
          title={playing ? 'Pause' : 'Play the session through'}>
          {playing ? '❚❚ Pause' : '▶ Play'}
        </button>
        <button type="button" className="ua-topn" title="Next action"
          disabled={i >= last}
          onClick={() => { setPlaying(false); setI((n) => Math.min(last, n + 1)); }}>▶</button>
        <button type="button" className="ua-topn" title="Jump to the end"
          onClick={() => { setPlaying(false); setI(last); }}>⏭</button>
        <span className="ua-player-speeds">
          {SPEEDS.map((s) => (
            <button key={s} type="button" className={`ua-topn${speed === s ? ' on' : ''}`}
              onClick={() => setSpeed(s)} title={`Play at ${s}× real time`}>{s}×</button>
          ))}
        </span>
        <span className="ua-player-pos">
          step {i + 1} of {frames.length} · +{fmtDuration(f.step.offset_s)}
        </span>
      </div>

      <input type="range" className="ua-player-scrub" min={0} max={last} value={i}
        aria-label="Scrub through the session"
        onChange={(e) => { setPlaying(false); setI(Number(e.target.value)); }} />

      <div className={`ua-player-now${f.step.paused ? ' paused' : ''}`}>
        <span className={`ua-trace-kind ua-kind-${f.step.kind}`}>{f.step.kind}</span>
        <strong>{f.step.label}</strong>
        {f.step.value !== null && f.step.value !== undefined && (
          <em>&nbsp;→ {String(f.step.value)}</em>
        )}
        {f.step.dwell_s ? <em>&nbsp;({fmtDuration(f.step.dwell_s)} on screen)</em> : null}
        {f.step.paused && (
          <span className="ua-trace-gap">after {fmtDuration(f.step.gap_s)} idle</span>
        )}
      </div>

      <div className="ua-player-state">
        <div className="ua-player-state-head">
          <span>Mode <strong>{f.mode || '—'}</strong></span>
          <span>Source <strong>{f.source || '—'}</strong></span>
          <span className="ua-player-state-note">
            {keys.length
              ? `${keys.length} setting${keys.length === 1 ? '' : 's'} in force`
              : 'no filter state recorded yet at this point'}
          </span>
        </div>
        {keys.length > 0 && (
          <div className="ua-player-grid">
            {keys.map((k) => (
              <div key={k}
                className={`ua-player-cell${f.changed.includes(k) ? ' changed' : ''}`}>
                <span className="ua-player-key">{k.replace(/_/g, ' ')}</span>
                <span className="ua-player-val">{showValue(f.filters[k])}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionTimeline;
